import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Link2, Cloud } from "lucide-react";

const PROVIDERS = [
    { test: /sharepoint\.com/i, label: "SharePoint" },
    { test: /onedrive\.live\.com|1drv\.ms|onedrive\.com/i, label: "OneDrive" },
    { test: /drive\.google\.com|docs\.google\.com/i, label: "Google Drive" },
    { test: /dropbox\.com/i, label: "Dropbox" },
    { test: /box\.com/i, label: "Box" },
];

export const isLinkDoc = (att) => att?.kind === "link" && !!att.url;
export const isValidUrl = (u) => /^https?:\/\//i.test((u || "").trim());
export const linkProvider = (url) => PROVIDERS.find((p) => p.test.test(url || ""))?.label || "Lien web";
export const linkHost = (url) => { try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return url; } };

export function LinkProviderBadge({ url, className = "" }) {
    return (
        <span className={`inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100 whitespace-nowrap ${className}`} title={url}>
            <Cloud className="w-2.5 h-2.5" />{linkProvider(url)}
        </span>
    );
}

// Dialog générique "Ajouter un lien externe" (OneDrive / SharePoint / ...). Seule l'URL est stockée.
export function LinkDocDialog({ open, onClose, onSubmit, title = "Ajouter un lien vers un document", typeOptions = null, defaultType = "", showDate = false, dateLabel = "Date du document", submitting = false, hint }) {
    const [url, setUrl] = useState("");
    const [name, setName] = useState("");
    const [type, setType] = useState(defaultType);
    const [date, setDate] = useState("");

    useEffect(() => {
        if (open) { setUrl(""); setName(""); setType(defaultType); setDate(new Date().toISOString().slice(0, 10)); }
    }, [open, defaultType]);

    const valid = isValidUrl(url);

    const submit = () => {
        if (!valid) return;
        onSubmit({ url: url.trim(), name: name.trim(), type, date });
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="link-doc-dialog">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><Link2 className="w-4 h-4 text-sky-600" />{title}</DialogTitle></DialogHeader>
                <p className="text-xs text-gray-500 -mt-2">Collez un lien de partage OneDrive, SharePoint, Google Drive… Le fichier reste chez vous, seul le lien est enregistré.</p>
                <div className="space-y-3 py-1">
                    <div>
                        <label className="block text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1">Lien (URL) *</label>
                        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…sharepoint.com/… ou https://1drv.ms/…" className="font-mono text-xs" autoFocus data-testid="link-doc-url" />
                        {url && !valid && <p className="text-[10px] text-red-500 mt-1">L'URL doit commencer par http:// ou https://</p>}
                        {valid && <p className="text-[10px] text-sky-600 mt-1">{linkProvider(url)} · {linkHost(url)}</p>}
                    </div>
                    <div>
                        <label className="block text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1">Nom affiché</label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="ex : Facture Dell 2026-03, Contrat maintenance…" data-testid="link-doc-name" />
                    </div>
                    {typeOptions && (
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1">Type</label>
                                <select value={type} onChange={(e) => setType(e.target.value)} className="w-full h-9 px-2 text-sm border rounded-md bg-white" data-testid="link-doc-type">
                                    {typeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                            {(showDate === true || (typeof showDate === "function" && showDate(type))) && (
                                <div>
                                    <label className="block text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1">{dateLabel}</label>
                                    <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="link-doc-date" />
                                </div>
                            )}
                        </div>
                    )}
                    {hint && <p className="text-[10px] text-gray-400">{hint}</p>}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Annuler</Button>
                    <Button onClick={submit} disabled={!valid || submitting} data-testid="link-doc-submit">{submitting ? "..." : "Ajouter le lien"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
